import { api, bearer } from './client';
import type { components } from './generated/entitlements';

export type MyAccessRequest = components['schemas']['AccessRequestView'];
export type AdminAccessRequest = components['schemas']['AdminAccessRequestView'];

export const REQUEST_ERRORS: Record<string, string> = {
  product_not_active: "Your organisation no longer has this application.",
  already_has_role: 'You already have access to this application — refresh to see it.',
  org_admin: "Organisation admins don't request access — ask another admin to give you the role on the Users page.",
  no_membership: "We couldn't find your membership of this organisation. Sign out and back in, then try again.",
  membership_inactive: 'Your membership of this organisation is suspended. Ask your organisation admin.',
  unknown_product: "That application isn't available.",
  workos_failed: "The identity service couldn't complete the action — try again shortly.",
  audit_unavailable: "The request couldn't be recorded to the audit log — try again shortly.",
};

export function requestErrorText(status: number | undefined, code: string | undefined): string {
  if (status === 429) return 'Too many requests. Wait a minute and try again.';
  return (code && REQUEST_ERRORS[code]) || "We couldn't send your request. Please try again.";
}

export const ADMIN_ACTION_ERRORS: Record<string, string> = {
  not_found: 'That request is no longer there.',
  not_open: 'That request was already handled.',
  invalid_roles: 'That role is not available for this application.',
  invalid_request: 'That request was not valid. Refresh and try again.',
  cannot_change_own_role: "You can't change your own organisation role.",
  pending_invite: "This person hasn't accepted their invitation yet.",
  forbidden: "You don't have permission to manage users.",
  workos_failed: "The identity service couldn't complete the action — try again shortly.",
  audit_unavailable: "The action couldn't be recorded to the audit log — try again shortly.",
};

export function adminActionErrorText(status: number | undefined, code: string | undefined): string {
  if (status === 429) return 'Too many attempts. Wait a minute and try again.';
  return (code && ADMIN_ACTION_ERRORS[code]) || 'The action failed — please try again in a moment.';
}

export function listMyAccessRequests(token: string, signal?: AbortSignal) {
  return api.GET('/access-requests/mine', { headers: bearer(token), signal });
}

export function createAccessRequest(token: string, productId: string) {
  return api.POST('/access-requests', { body: { productId }, headers: bearer(token) });
}

export function listAccessRequests(token: string, signal?: AbortSignal) {
  return api.GET('/access-requests', { headers: bearer(token), signal });
}

export function grantAccessRequest(token: string, id: string, roleSlug: string) {
  return api.POST('/access-requests/{id}/grant', { params: { path: { id } }, body: { roleSlug }, headers: bearer(token) });
}

export function dismissAccessRequest(token: string, id: string) {
  return api.POST('/access-requests/{id}/dismiss', { params: { path: { id } }, headers: bearer(token) });
}

export function grantAllAccessRequests(token: string, productId: string, roleSlug: string) {
  return api.POST('/access-requests/grant-all', { body: { productId, roleSlug }, headers: bearer(token) });
}

export function dismissAllAccessRequests(token: string, productId: string) {
  return api.POST('/access-requests/dismiss-all', { body: { productId }, headers: bearer(token) });
}
